import {gql} from '@apollo/client';

import {
  AntibodyCurrentVersionIdQuery,
  AntibodyCurrentVersionIdQueryVariables,
} from 'antibodies/__generated/AntibodyCurrentVersionIdQuery';
import {IsAntibodyQuery, IsAntibodyQueryVariables} from 'antibodies/__generated/IsAntibodyQuery';
import {updateAntibodyUIOpenVersionId} from 'antibodies/hooks/useAntibodyUIState';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import APIUtil from 'util/APIUtil';

export interface AntibodyStateData {
  isAntibody: boolean;
}

const IS_ANTIBODY_QUERY = gql`
  query IsAntibodyQuery($fileId: StrictString!) @skipAudit @engComponent(name: ANTIBODIES) {
    bioentity(id: $fileId) {
      ... on BasicFolderItemNode {
        id
        tagSchema {
          ... on TagSchemaNode {
            id
            itemType
          }
        }
      }
    }
  }
`;

const ANTIBODY_CURRENT_VERSION_ID_QUERY = gql`
  query AntibodyCurrentVersionIdQuery($antibodyId: StrictString!) @skipAudit @engComponent(name: ANTIBODIES) {
    antibody(antibodyId: $antibodyId) {
      ... on AntibodyNode {
        id
        currentVersionId
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Queries for whether a file is an antibody and initializes the antibody state data if so.
 * @param fileId The API ID of the file to check
 */
export async function initializeAntibodyDataOnOpen(fileId: string): Promise<AntibodyStateData | null> {
  if (!(await getIsAntibody(fileId))) {
    return null;
  }

  const {antibody} = await doQuerySafe<AntibodyCurrentVersionIdQuery, AntibodyCurrentVersionIdQueryVariables>(
    {query: ANTIBODY_CURRENT_VERSION_ID_QUERY},
    {variables: {antibodyId: fileId}}
  );

  // This is the expected result if the file is truly inaccessible, but it is also the expected result if the
  // item is not an antibody. At this point, we should know it's both an antibody and accessible from the
  // previous query, but check anyway in case of race conditions.
  // Note that relying on a non-antibody being inaccessible is not valid, as it doesn't work with our graphql
  // caching strategy.
  if (isInaccessible(antibody)) {
    return null;
  }

  updateAntibodyUIOpenVersionId(fileId, antibody.currentVersionId);
  return {isAntibody: true};
}

/**
 * Queries for whether a file is an antibody. Returns false if the file is inaccessible.
 * @param fileId The API ID of the file to check
 */
export async function getIsAntibody(fileId: string): Promise<boolean> {
  if (!APIUtil.isBasicFolderItem(fileId)) {
    return false;
  }

  const {bioentity: maybeAntibody} = await doQuerySafe<IsAntibodyQuery, IsAntibodyQueryVariables>(
    {query: IS_ANTIBODY_QUERY},
    {variables: {fileId}}
  );

  if (maybeAntibody.__typename !== 'BasicFolderItemNode') {
    return false;
  }

  return maybeAntibody.tagSchema?.itemType === TagSchemaItemType.ANTIBODY;
}
